/**
 * Type-safe обёртки модуля биллинга/фолио (#338).
 *
 * Все запросы идут через customFetch (инъекция bearer JWT + X-Tenant-Slug).
 * Бэкенд: `internal/billing/http_app`:
 *   GET   /v1/me/folio                       — гость: свои фолио + total_unpaid (ownership)
 *   GET   /v1/folios                         — персонал/менеджер: все фолио тенанта
 *   GET   /v1/folios/{id}                    — персонал: детали фолио + позиции
 *   POST  /v1/folios/{id}/line-items         — персонал: добавить позицию
 *   PATCH /v1/folios/{id}/line-items/{lid}   — персонал: сторнировать позицию
 *   POST  /v1/folios/{id}/settlements        — персонал: записать оплату (отметить оплаченным)
 *   PATCH /v1/folios/{id}                     — персонал: сменить статус фолио
 *
 * RBAC: мутации /v1/folios** гейтятся route-rule в cmd/api/main.go
 * (staffPlus); гостю отвечают 403. Гостевой /v1/me/folio проверяет ownership.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  AddFolioLineInput,
  FolioDetail,
  FolioListResponse,
  MyFolioResponse,
  RecordSettlementInput,
  SetFolioStatusInput,
} from '@hsc/types';

/** GET /v1/me/folio — счета текущего гостя + общий неоплаченный итог. */
export async function getMyFolio(): Promise<MyFolioResponse> {
  return customFetch<MyFolioResponse>({ url: '/me/folio', method: 'GET' });
}

/** GET /v1/folios — все фолио тенанта (менеджер/персонал). */
export async function listFolios(params?: {
  limit?: number;
  offset?: number;
}): Promise<FolioListResponse> {
  const q: Record<string, number> = {};
  if (params?.limit !== undefined) q['limit'] = params.limit;
  if (params?.offset !== undefined) q['offset'] = params.offset;
  return customFetch<FolioListResponse>(
    Object.keys(q).length > 0
      ? { url: '/folios', method: 'GET', params: q }
      : { url: '/folios', method: 'GET' },
  );
}

/** GET /v1/folios/{id} — детали фолио с позициями (персонал). */
export async function getFolioDetail(id: string): Promise<FolioDetail> {
  return customFetch<FolioDetail>({
    url: `/folios/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

/** POST /v1/folios/{id}/line-items — добавить позицию в счёт гостя (персонал). */
export async function addFolioLineItem(
  folioId: string,
  input: AddFolioLineInput,
): Promise<FolioDetail> {
  return customFetch<FolioDetail>({
    url: `/folios/${encodeURIComponent(folioId)}/line-items`,
    method: 'POST',
    data: input,
  });
}

/** PATCH /v1/folios/{id}/line-items/{lid} — сторнировать позицию (персонал). */
export async function reverseFolioLineItem(
  folioId: string,
  lineItemId: string,
  reason?: string,
): Promise<FolioDetail> {
  return customFetch<FolioDetail>({
    url: `/folios/${encodeURIComponent(folioId)}/line-items/${encodeURIComponent(lineItemId)}`,
    method: 'PATCH',
    data: reason ? { reason } : {},
  });
}

/** POST /v1/folios/{id}/settlements — записать оплату / отметить оплаченным (персонал). */
export async function recordSettlement(
  folioId: string,
  input: RecordSettlementInput,
): Promise<FolioDetail> {
  return customFetch<FolioDetail>({
    url: `/folios/${encodeURIComponent(folioId)}/settlements`,
    method: 'POST',
    data: input,
  });
}

/** PATCH /v1/folios/{id} — сменить статус фолио (персонал, settled/closed/…). */
export async function setFolioStatus(
  folioId: string,
  input: SetFolioStatusInput,
): Promise<FolioDetail> {
  return customFetch<FolioDetail>({
    url: `/folios/${encodeURIComponent(folioId)}`,
    method: 'PATCH',
    data: input,
  });
}
